import React, { useState, useEffect } from 'react';
import './EmailVerificationOTP.css';
import { toast } from 'react-toastify';
import apiService from '../services/apiService';

function EmailVerificationOTP({ email, onVerificationComplete, onBackToSignup }) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [timeLeft, setTimeLeft] = useState(600); // 10 minutes
  const [canResend, setCanResend] = useState(false);

  // Timer for OTP expiration
  useEffect(() => {
    if (timeLeft <= 0) {
      setCanResend(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();

    if (otp.length !== 6) {
      toast.error('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      await apiService.post('/auth/verify-email-otp', {
        email,
        otp,
      });

      toast.success('Email verified successfully! You can now log in.');
      onVerificationComplete();
    } catch (error) {
      console.error('OTP verification failed:', error);
      toast.error(error.response?.data?.detail || 'OTP verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setResendLoading(true);
    try {
      await apiService.post('/auth/send-email-verification-otp', {
        email,
      });

      toast.success('OTP resent to your email');
      setTimeLeft(600);
      setCanResend(false);
      setOtp('');
    } catch (error) {
      console.error('Resend OTP failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="email-verification-overlay" onClick={onBackToSignup}>
      <div className="email-verification-container" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onBackToSignup}>&times;</button>

        <div className="verification-header">
          <div className="verification-icon">✉️</div>
          <h2>Verify Your Email</h2>
          <p className="verification-subtitle">
            We've sent a verification code to<br />
            <strong>{email}</strong>
          </p>
        </div>

        <form onSubmit={handleVerifyOtp} className="verification-form">
          <div className="form-group">
            <label htmlFor="otp-input">Enter 6-Digit OTP</label>
            <input
              type="text"
              id="otp-input"
              value={otp}
              onChange={handleOtpChange}
              placeholder="000000"
              maxLength="6"
              className="otp-input"
              disabled={loading}
              autoFocus
            />
            <p className="otp-hint">
              {timeLeft > 0 ? (
                <>
                  OTP expires in: <span className="time-left">{formatTime(timeLeft)}</span>
                </>
              ) : (
                <span className="otp-expired">OTP has expired. Please request a new one.</span>
              )}
            </p>
          </div>

          <button
            type="submit"
            className="verify-button"
            disabled={loading || otp.length !== 6 || timeLeft <= 0}
          >
            {loading ? 'Verifying...' : 'Verify Email'}
          </button>

          <div className="resend-section">
            <p className="resend-text">Didn't receive the code?</p>
            <button
              type="button"
              className="resend-button"
              onClick={handleResendOtp}
              disabled={!canResend || resendLoading || timeLeft > 300}
            >
              {resendLoading ? 'Sending...' : 'Resend OTP'}
            </button>
          </div>

          <button
            type="button"
            className="back-button"
            onClick={onBackToSignup}
          >
            Back to Sign Up
          </button>
        </form>

        <div className="verification-info">
          <p>
            <strong>Note:</strong> Check your spam folder if you don't see the email in your inbox.
          </p>
        </div>
      </div>
    </div>
  );
}

export default EmailVerificationOTP;
